import {
  Type,
  TSchema,
  TUnion,
  TOptional,
  TString,
  TDate,
  TNull,
  TInteger,
  TNumber,
  TBoolean,
  TArray,
  TAny,
} from '@sinclair/typebox';

/**
 * Field Types - Direct PostgreSQL type mapping
 * No guessing, no complex detection - explicit is better!
 */

// Shared field metadata type used across schema helpers
/** Аргумент для функциональной формы $error */
export type FieldValidationError = {
  message: string;
  path: string;
  schema: Record<string, unknown>;
};

export type FieldMeta = {
  $fieldType?: string;
  $maxLength?: number;
  $precision?: number;
  $scale?: number;
  $minimum?: number;
  $maximum?: number;
  $nullable?: boolean;
  $unique?: boolean;
  $default?: unknown;
  $defaultRaw?: string;
  $serialize?: (v: unknown) => string;
  $description?: string;
  $index?: boolean; // TODO реализовать поддержку типа индекса
  $isRelation?: boolean;
  $enumValues?: string[];
  /** Кастомное сообщение об ошибке валидации. Строка или функция для динамического формирования. */
  $error?: string | ((error: FieldValidationError) => string);
  $foreignKey?: {
    table: string;
    column: string;
    onDelete?: 'CASCADE' | 'SET NULL' | 'RESTRICT';
    onUpdate?: 'CASCADE' | 'SET NULL' | 'RESTRICT';
  };
  $relationName?: string; // Name for the relation object field (e.g., 'manager' for 'manager_id')
  // tableName + имена приватных полей целевой схемы (для oracle-гарда orderBy по связи).
  // Только имена, не полная схема — полная вызывала рекурсивное раздувание meta().
  $targetSchema?: { tableName: string; privateFields?: string[] };
  /**
   * Приватное поле (секрет): password_hash, api_token_hash и т.п. Бинарный флаг.
   *
   * Защита — на границе клиента (не бабиситтинг серверного кода):
   * - вырезается из любого RPC-ответа egress-страховкой (private-fields-registry);
   * - запрещён в where/orderBy/select типизированного билдера (oracle-канал).
   * Серверный код читает секрет свободно (SELECT его включает); для where по секрету —
   * сырой this._knex (escape hatch). Раскрытие клиенту всё равно режет egress.
   */
  $private?: boolean;
};

// Exported FieldSchema type for reuse across helpers
export type FieldSchema<T extends TSchema = TSchema> = T & Partial<FieldMeta>;

// Common option shapes for field factories
type BaseFieldOptions = {
  title?: string;
  description?: string;
  nullable?: boolean;
  unique?: boolean;
  default?: unknown;
  /** Raw SQL expression for DEFAULT (e.g. 'CURRENT_TIMESTAMP', 'gen_random_uuid()') */
  defaultRaw?: string;
  /** Кастомное сообщение об ошибке валидации (отображается вместо стандартного TypeBox-сообщения) */
  error?: string | ((error: FieldValidationError) => string);
  /** Приватное поле (секрет): исключается из выборок и relation-JSON, см. FieldMeta.$private */
  private?: boolean;
};
// JSON Schema-like constraints for TypeBox primitives
type StringConstraints = {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: string;
  contentEncoding?: string;
  contentMediaType?: string;
};

type NumericConstraints = {
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  multipleOf?: number;
};

type ArrayConstraints = {
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
};

type StringFieldOptions = BaseFieldOptions & StringConstraints;
type TextFieldOptions = BaseFieldOptions & StringConstraints;

// StringField - VARCHAR with explicit length
export function StringField<TNullable extends boolean | undefined = undefined>(
  options: StringFieldOptions & { nullable?: TNullable } = {} as StringFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString> {
  const baseSchema = Type.String({
    ...options,
    description: options.description ?? 'String field',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;

  const field = schema as FieldSchema<typeof schema>;

  field.$fieldType = 'varchar';
  field.$maxLength = options.maxLength ?? 255;
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  if (options.private) field.$private = true;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as unknown as FieldSchema<typeof schema>;
}

// TextField - TEXT for unlimited length
export function TextField<TNullable extends boolean | undefined = undefined>(
  options: TextFieldOptions & { nullable?: TNullable } = {} as TextFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString> {
  const baseSchema = Type.String({
    ...options,
    description: options.description ?? 'Text field (unlimited length)',
  });
  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'text';
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  if (options.private) field.$private = true;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as unknown as FieldSchema<typeof schema>;
}

// IntegerField - INTEGER
type IntegerFieldOptions = BaseFieldOptions & NumericConstraints;
export function IntegerField<TNullable extends boolean | undefined = undefined>(
  options: IntegerFieldOptions & { nullable?: TNullable } = {} as IntegerFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TInteger, TNull]>> : TInteger> {
  const baseSchema = Type.Integer({
    ...options,
    description: options.description ?? 'Integer field',
  });
  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'integer';
  if (options.minimum !== undefined) field.$minimum = options.minimum;
  if (options.maximum !== undefined) field.$maximum = options.maximum;
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// BigIntField - BIGINT (64-bit integer для значений > 2^31, например Telegram Bot ID)
type BigIntFieldOptions = BaseFieldOptions & NumericConstraints;
export function BigIntField<TNullable extends boolean | undefined = undefined>(
  options: BigIntFieldOptions & { nullable?: TNullable } = {} as BigIntFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TInteger, TNull]>> : TInteger> {
  const baseSchema = Type.Integer({
    ...options,
    description: options.description ?? 'BigInt field (64-bit)',
  });
  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'bigint';
  if (options.minimum !== undefined) field.$minimum = options.minimum;
  if (options.maximum !== undefined) field.$maximum = options.maximum;
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// DecimalField - DECIMAL with precision/scale
type DecimalFieldOptions = BaseFieldOptions &
  NumericConstraints & { precision?: number; scale?: number };
export function DecimalField<TNullable extends boolean | undefined = undefined>(
  options: DecimalFieldOptions & { nullable?: TNullable } = {} as DecimalFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TNumber, TNull]>> : TNumber> {
  const baseSchema = Type.Number({
    ...options,
    description: options.description ?? 'Decimal field',
  });
  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'decimal';
  field.$precision = options.precision ?? 10;
  field.$scale = options.scale ?? 2;
  if (options.minimum !== undefined) field.$minimum = options.minimum;
  if (options.maximum !== undefined) field.$maximum = options.maximum;
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// TimestampField - TIMESTAMP (handles Date objects and ISO strings)
export function TimestampField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<
  TNullable extends true ? TOptional<TUnion<[TString, TDate, TNull]>> : TUnion<[TString, TDate]>
> {
  const schema = options.nullable
    ? Type.Optional(
        Type.Union([Type.String({ format: 'date-time' }), Type.Date(), Type.Null()], {
          ...options,
          description: options.description || 'Timestamp field',
        })
      )
    : Type.Union([Type.String({ format: 'date-time' }), Type.Date()], {
        ...options,
        description: options.description || 'Timestamp field',
      });
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'timestamp';
  field.$nullable = !!options.nullable;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// DateField - DATE (календарная дата без времени; хранится и передаётся как строка 'YYYY-MM-DD')
//
// В отличие от TimestampField (timestamp без TZ + объект Date с «UTC-полдень»),
// DateField — это чистая строка YYYY-MM-DD сквозняком клиент → сервер → БД (DATE).
// Никаких пересчётов таймзон: день не «уезжает» на ±1. Валидацию формата даёт pattern,
// семантику невалидных дат (напр. 2026-13-40) добивает сам PostgreSQL при вставке.
export function DateField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString> {
  const baseSchema = Type.String({
    pattern: '^\\d{4}-\\d{2}-\\d{2}$',
    ...options,
    description: options.description || 'Calendar date field (YYYY-MM-DD, без времени)',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'date';
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// BooleanField - BOOLEAN
export function BooleanField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TBoolean, TNull]>> : TBoolean> {
  const baseSchema = Type.Boolean({
    ...options,
    description: options.description || 'Boolean field',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'boolean';
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// JsonField - JSONB
export function JsonField<TNullable extends boolean | undefined = undefined>(
  schema?: TSchema,
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TAny, TNull]>> : TAny> {
  const baseSchema = schema ? schema : Type.Any();
  const res = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = res as FieldSchema<typeof res>;
  field.$fieldType = 'jsonb';
  field.$serialize = (value: unknown) => JSON.stringify(value);
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  Object.assign(field, options);
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof res>;
}

// ArrayField - PostgreSQL native arrays
type ArrayFieldOptions = BaseFieldOptions & ArrayConstraints;

export function ArrayField<TNullable extends boolean | undefined = undefined>(
  itemType: TSchema,
  options: ArrayFieldOptions & { nullable?: TNullable } = {} as ArrayFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TArray, TNull]>> : TArray> {
  const baseSchema = Type.Array(itemType, {
    ...options,
    description: options.description || 'Array field',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;

  // Determine PostgreSQL array type based on item type
  const itemFieldType = (itemType as FieldSchema).$fieldType;
  if (typeof itemFieldType === 'string') {
    field.$fieldType = `${itemFieldType}[]`;
  } else {
    // Fallback to TypeBox Kind detection
    const kind = (itemType as unknown as Record<symbol, unknown>)[Symbol.for('TypeBox.Kind')];
    switch (kind) {
      case 'String':
        field.$fieldType = 'text[]';
        break;
      case 'Integer':
        field.$fieldType = 'integer[]';
        break;
      case 'Number':
        field.$fieldType = 'numeric[]';
        break;
      case 'Boolean':
        field.$fieldType = 'boolean[]';
        break;
      default:
        field.$fieldType = 'text[]'; // Default to text array
    }
  }

  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// UuidField - UUID
export function UuidField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString> {
  const baseSchema = Type.String({
    format: 'uuid',
    ...options,
    description: options.description || 'UUID field',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'uuid';
  field.$defaultRaw = 'gen_random_uuid()'; // PostgreSQL default for UUID generation
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// TimeField - TIME field stored as HH:MM or HH:MM:SS string
export function TimeField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString> {
  const baseSchema = Type.String({
    pattern: '^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$',
    minLength: 5,
    maxLength: 8,
    ...options,
    description: options.description || 'Time field in HH:MM or HH:MM:SS format (24-hour)',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'time';
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

// SlugField - UPPERCASE constant identifiers for reference tables
export function SlugField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString> {
  const baseSchema = Type.String({
    minLength: 3,
    maxLength: 30,
    pattern: '^[A-Z][A-Z0-9_]{2,29}$',
    ...options,
    description: options.description || 'Slug field (UPPERCASE_STYLE, 3-30 chars, letter first)',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'varchar';
  field.$maxLength = 30;
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

/**
 * Geometry: Point (x,y) mapped to PostgreSQL 'point'
 */
export function PointField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TAny, TNull]>> : TAny> {
  const baseSchema = Type.Any({
    ...options,
    description: options.description || 'Point (x,y)',
  });
  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'point';
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}

/**
 * Geometry: Box (rectangle) mapped to PostgreSQL 'box'
 */
export function BoxField<TNullable extends boolean | undefined = undefined>(
  options: BaseFieldOptions & { nullable?: TNullable } = {} as BaseFieldOptions & {
    nullable?: TNullable;
  }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TAny, TNull]>> : TAny> {
  const baseSchema = Type.Any({
    ...options,
    description: options.description || 'Rectangle box ((x1,y1),(x2,y2))',
  });
  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;
  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'box';
  field.$nullable = !!options.nullable;
  field.$unique = !!options.unique;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // @ts-expect-error - TypeScript can't match union variable with conditional return type
  return field as FieldSchema<typeof schema>;
}
/**
 * Creates an enum field that maps to PostgreSQL TEXT
 * Simpler than PostgreSQL ENUM type - no ALTER TYPE needed for value changes
 */
export function EnumField<TNullable extends boolean | undefined = undefined>(
  values: readonly string[],
  options: BaseFieldOptions & {
    nullable?: TNullable;
    default?: string;
  } = {} as BaseFieldOptions & { nullable?: TNullable; default?: string }
): FieldSchema<TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString> {
  // Runtime validation: restrict to provided literal values
  const literals = values.map(v => Type.Literal(v));
  const baseSchema = Type.Union(literals as unknown as [TSchema, ...TSchema[]], {
    title: options.title,
    default: options.default,
    description: options.description || 'Enum field',
  });

  const schema = options.nullable
    ? Type.Optional(Type.Union([baseSchema, Type.Null()], { title: options.title }))
    : baseSchema;

  const field = schema as FieldSchema<typeof schema>;
  field.$fieldType = 'enum';
  field.$enumValues = [...values];
  field.$nullable = !!options.nullable;
  field.$description = options.description;
  if (options.default !== undefined) field.$default = options.default;
  if (options.defaultRaw !== undefined) field.$defaultRaw = options.defaultRaw;
  if (options.error !== undefined) field.$error = options.error;
  // TS: return broad string types while runtime schema is literal-union
  return field as FieldSchema<
    TNullable extends true ? TOptional<TUnion<[TString, TNull]>> : TString
  >;
}
